import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Royal Gems Diamond Cut Designer - Admin Tool
 *
 * Interactive tool for creating custom diamond cuts with real-time 3D preview.
 * Allows admins to adjust all diamond proportions using sliders.
 */
import { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaGem, FaUndo, FaDownload, FaCheckCircle, FaExclamationTriangle, FaBug } from 'react-icons/fa';
import DiamondViewer3D from '../../components/Diamond/Viewer3D/DiamondViewer3D';
import { getDiamondProportionsSummary, isExcellentCut } from '../../components/Diamond/Viewer3D/DataDrivenDiamond';
import { PageContainer, PageTitle, Text, SectionTitle, ContentSection } from '../../components/PageStyles';
const SLIDER_CONFIGS = [
    {
        label: 'Table Size',
        key: 'tablePercent',
        min: 45,
        max: 70,
        step: 0.5,
        defaultValue: 56,
        unit: '%',
        description: 'Size of the top facet as percentage of diameter',
        idealRange: [53, 58]
    },
    {
        label: 'Total Depth',
        key: 'depthPercent',
        min: 50,
        max: 75,
        step: 0.5,
        defaultValue: 61,
        unit: '%',
        description: 'Total height as percentage of diameter',
        idealRange: [59, 62.6]
    },
    {
        label: 'Crown Angle',
        key: 'crownAngle',
        min: 25,
        max: 40,
        step: 0.5,
        defaultValue: 34.5,
        unit: '°',
        description: 'Angle of crown facets from horizontal',
        idealRange: [33.7, 35.8]
    },
    {
        label: 'Pavilion Angle',
        key: 'pavilionAngle',
        min: 38,
        max: 43,
        step: 0.5,
        defaultValue: 40.8,
        unit: '°',
        description: 'Angle of pavilion facets from horizontal',
        idealRange: [40.6, 41.8]
    }
];
const DiamondCutDesigner = () => {
    // Initialize with GIA Excellent cut defaults
    const [proportions, setProportions] = useState({
        tablePercent: 56,
        depthPercent: 61,
        crownAngle: 34.5,
        pavilionAngle: 40.8
    });
    const [caratWeight, setCaratWeight] = useState(1.0);
    const [color, setColor] = useState('G');
    const [clarity, setClarity] = useState('VS1');
    const [cut, setCut] = useState('Excellent');
    const [glassMode, setGlassMode] = useState(false);
    const viewerRef = useRef(null);
    const handleSliderChange = (key, value) => {
        setProportions(prev => ({
            ...prev,
            [key]: value
        }));
    };
    const resetToDefaults = () => {
        setProportions({
            tablePercent: 56,
            depthPercent: 61,
            crownAngle: 34.5,
            pavilionAngle: 40.8
        });
    };
    const exportConfiguration = () => {
        const config = {
            proportions,
            caratWeight,
            color,
            clarity,
            cut,
            isExcellent: isExcellentCut(proportions),
            exportedAt: new Date().toISOString()
        };
        console.log('Export Configuration:', config);
        // Download as JSON file
        const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `diamond-config-${new Date().toISOString()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };
    const exportDebugData = () => {
        if (!viewerRef.current?.getDebugData) {
            alert('Debug data not available. Viewer not ready.');
            return;
        }
        const debugData = viewerRef.current.getDebugData();
        const fullDebug = {
            diamond: {
                proportions,
                caratWeight,
                color,
                clarity,
                cut,
                materialMode: glassMode ? 'glass' : 'solid',
                isExcellentCut: isExcellentCut(proportions),
                summary: getDiamondProportionsSummary(proportions)
            },
            geometry: debugData,
            exportedAt: new Date().toISOString(),
            version: '1.0.0'
        };
        console.log('🔷 FULL DIAMOND DEBUG DATA:', fullDebug);
        // Download as JSON file
        const blob = new Blob([JSON.stringify(fullDebug, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `diamond-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
        a.click();
        URL.revokeObjectURL(url);
        alert('Debug data exported! Check console and downloads folder.');
    };
    const isInIdealRange = (value, idealRange) => {
        if (!idealRange)
            return true;
        return value >= idealRange[0] && value <= idealRange[1];
    };
    const isExcellent = isExcellentCut(proportions);
    const summary = getDiamondProportionsSummary(proportions);
    return (_jsxs(PageContainer, { children: [_jsxs(Header, { children: [_jsxs("div", { children: [_jsxs(PageTitle, { children: [_jsx(FaGem, { style: { marginRight: '12px', color: 'var(--accent-color)' } }), "Royal Gems Diamond Cut Designer"] }), _jsx(Text, { children: "Create custom diamond cuts with real-time 3D preview" })] }), _jsx(CutQualityBadge, { "$excellent": isExcellent, children: isExcellent ? (_jsxs(_Fragment, { children: [_jsx(FaCheckCircle, { style: { marginRight: '8px' } }), "GIA Excellent Cut"] })) : (_jsxs(_Fragment, { children: [_jsx(FaExclamationTriangle, { style: { marginRight: '8px' } }), "Custom Cut"] })) })] }), _jsxs(MainContent, { children: [_jsxs(ControlsPanel, { children: [_jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Diamond Proportions" }), _jsx(SectionDescription, { children: "Adjust the key proportions that define the diamond's cut quality" }), SLIDER_CONFIGS.map(config => {
                                        const value = proportions[config.key] || config.defaultValue;
                                        const inRange = isInIdealRange(value, config.idealRange);
                                        return (_jsxs(SliderControl, { children: [_jsxs(SliderHeader, { children: [_jsx(SliderLabel, { children: config.label }), _jsxs(SliderValue, { "$inRange": inRange, children: [value.toFixed(1), config.unit, config.idealRange && (_jsx(IdealBadge, { "$inRange": inRange, children: inRange ? '✓ Ideal' : `Ideal: ${config.idealRange[0]}-${config.idealRange[1]}${config.unit}` }))] })] }), _jsx(SliderDescription, { children: config.description }), _jsxs(SliderTrack, { children: [_jsx(SliderInput, { type: "range", min: config.min, max: config.max, step: config.step, value: value, onChange: (e) => handleSliderChange(config.key, parseFloat(e.target.value)) }), _jsx(SliderFill, { style: {
                                                                width: `${((value - config.min) / (config.max - config.min)) * 100}%`
                                                            }, "$inRange": inRange }), config.idealRange && (_jsx(IdealRangeIndicator, { style: {
                                                                left: `${((config.idealRange[0] - config.min) / (config.max - config.min)) * 100}%`,
                                                                width: `${((config.idealRange[1] - config.idealRange[0]) / (config.max - config.min)) * 100}%`
                                                            } }))] }), _jsxs(SliderMinMax, { children: [_jsxs("span", { children: [config.min, config.unit] }), _jsxs("span", { children: [config.max, config.unit] })] })] }, config.key));
                                    })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Additional Properties" }), _jsxs(AdditionalControls, { children: [_jsxs(ControlRow, { children: [_jsx(ControlLabel, { children: "Carat Weight:" }), _jsx(SmallSlider, { type: "range", min: 0.3, max: 5.0, step: 0.1, value: caratWeight, onChange: (e) => setCaratWeight(parseFloat(e.target.value)) }), _jsxs(ControlValue, { children: [caratWeight.toFixed(1), "ct"] })] }), _jsxs(ControlRow, { children: [_jsx(ControlLabel, { children: "Color:" }), _jsxs(Select, { value: color, onChange: (e) => setColor(e.target.value), children: [_jsx("option", { value: "D", children: "D (Colorless)" }), _jsx("option", { value: "E", children: "E (Colorless)" }), _jsx("option", { value: "F", children: "F (Colorless)" }), _jsx("option", { value: "G", children: "G (Near Colorless)" }), _jsx("option", { value: "H", children: "H (Near Colorless)" }), _jsx("option", { value: "I", children: "I (Near Colorless)" }), _jsx("option", { value: "J", children: "J (Near Colorless)" })] })] }), _jsxs(ControlRow, { children: [_jsx(ControlLabel, { children: "Clarity:" }), _jsxs(Select, { value: clarity, onChange: (e) => setClarity(e.target.value), children: [_jsx("option", { value: "FL", children: "FL (Flawless)" }), _jsx("option", { value: "IF", children: "IF (Internally Flawless)" }), _jsx("option", { value: "VVS1", children: "VVS1 (Very Very Slightly Included)" }), _jsx("option", { value: "VVS2", children: "VVS2" }), _jsx("option", { value: "VS1", children: "VS1 (Very Slightly Included)" }), _jsx("option", { value: "VS2", children: "VS2" }), _jsx("option", { value: "SI1", children: "SI1 (Slightly Included)" }), _jsx("option", { value: "SI2", children: "SI2" })] })] }), _jsxs(ControlRow, { children: [_jsx(ControlLabel, { children: "Cut Grade:" }), _jsxs(Select, { value: cut, onChange: (e) => setCut(e.target.value), children: [_jsx("option", { value: "Excellent", children: "Excellent" }), _jsx("option", { value: "Very Good", children: "Very Good" }), _jsx("option", { value: "Good", children: "Good" }), _jsx("option", { value: "Fair", children: "Fair" })] })] }), _jsxs(ControlRow, { children: [_jsx(ControlLabel, { children: "Material Mode:" }), _jsx(ToggleButton, { "$active": glassMode, onClick: () => setGlassMode(!glassMode), children: glassMode ? 'Glass (Transparent)' : 'Solid (Opaque)' })] })] })] }), _jsx(ContentSection, { children: _jsx(SummaryText, { children: summary }) }), _jsxs(ActionButtons, { children: [_jsxs(ActionButton, { onClick: resetToDefaults, children: [_jsx(FaUndo, { style: { marginRight: '8px' } }), "Reset to GIA Excellent"] }), _jsxs(ActionButton, { onClick: exportConfiguration, children: [_jsx(FaDownload, { style: { marginRight: '8px' } }), "Export Config"] }), _jsxs(ActionButton, { "$primary": true, onClick: exportDebugData, children: [_jsx(FaBug, { style: { marginRight: '8px' } }), "Debug Export"] })] })] }), _jsx(PreviewPanel, { children: _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Real-Time Preview" }), _jsxs(PreviewSubtitle, { children: [caratWeight.toFixed(1), "ct \u2022 ", color, " \u2022 ", clarity, " \u2022 ", cut] }), _jsx(PreviewContainer, { children: _jsx(DiamondViewer3D, { ref: viewerRef, shape: "round", caratWeight: caratWeight, color: color, clarity: clarity, cut: cut, tablePercent: proportions.tablePercent, depthPercent: proportions.depthPercent, crownAngle: proportions.crownAngle, pavilionAngle: proportions.pavilionAngle, enableAR: false, materialMode: glassMode ? 'glass' : 'solid' }) }), _jsxs(PreviewInfo, { children: [_jsxs(InfoItem, { children: [_jsx(InfoLabel, { children: "Vertices:" }), _jsx(InfoValue, { children: "65 (8-fold symmetry)" })] }), _jsxs(InfoItem, { children: [_jsx(InfoLabel, { children: "Facets:" }), _jsx(InfoValue, { children: "57 triangles" })] }), _jsxs(InfoItem, { children: [_jsx(InfoLabel, { children: "Table Size:" }), _jsxs(InfoValue, { children: [(proportions.tablePercent || 56).toFixed(1), "%"] })] }), _jsxs(InfoItem, { children: [_jsx(InfoLabel, { children: "Depth:" }), _jsxs(InfoValue, { children: [(proportions.depthPercent || 61).toFixed(1), "%"] })] })] })] }) })] })] }));
};
// Styled Components (Theme-aware using CSS variables)
const Header = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
const CutQualityBadge = styled.div `
  padding: 12px 20px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  background: ${props => props.$excellent
    ? 'var(--success-color)'
    : 'var(--warning-color)'};
  color: ${props => props.$excellent ? '#000' : '#000'};
  border: 2px solid ${props => props.$excellent
    ? 'var(--success-color)'
    : 'var(--warning-color)'};
`;
const MainContent = styled.div `
  display: grid;
  grid-template-columns: 500px 1fr;
  gap: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;
const ControlsPanel = styled.div `
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const SectionDescription = styled(Text) `
  margin-bottom: 20px;
`;
const SliderControl = styled.div `
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const SliderHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;
const SliderLabel = styled.label `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;
const SliderValue = styled.div `
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.$inRange ? 'var(--success-color)' : 'var(--warning-color)'};
  display: flex;
  align-items: center;
  gap: 8px;
`;
const IdealBadge = styled.span `
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 3px;
  background: ${props => props.$inRange
    ? 'rgba(0, 255, 0, 0.15)'
    : 'rgba(255, 165, 0, 0.15)'};
  color: ${props => props.$inRange ? 'var(--success-color)' : 'var(--warning-color)'};
  border: 1px solid ${props => props.$inRange ? 'var(--success-color)' : 'var(--warning-color)'};
`;
const SliderDescription = styled.p `
  font-size: 12px;
  color: var(--text-tertiary);
  margin-bottom: 12px;
`;
const SliderTrack = styled.div `
  position: relative;
  height: 6px;
  background: var(--bg-tertiary);
  border-radius: 3px;
  margin-bottom: 8px;
  border: 1px solid var(--border-color);
`;
const SliderInput = styled.input `
  position: absolute;
  width: 100%;
  height: 20px;
  top: -7px;
  opacity: 0;
  cursor: pointer;
  z-index: 2;

  &::-webkit-slider-thumb {
    cursor: pointer;
  }

  &::-moz-range-thumb {
    cursor: pointer;
  }
`;
const SliderFill = styled.div `
  position: absolute;
  height: 100%;
  background: ${props => props.$inRange
    ? 'var(--success-color)'
    : 'var(--warning-color)'};
  border-radius: 3px 0 0 3px;
  transition: all 0.2s;
`;
const IdealRangeIndicator = styled.div `
  position: absolute;
  height: 100%;
  background: rgba(0, 255, 0, 0.15);
  border-left: 2px solid var(--success-color);
  border-right: 2px solid var(--success-color);
  pointer-events: none;
`;
const SliderMinMax = styled.div `
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--text-tertiary);
`;
const AdditionalControls = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ControlRow = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
`;
const ControlLabel = styled.label `
  font-size: 14px;
  color: var(--text-secondary);
  min-width: 120px;
  font-weight: 500;
`;
const SmallSlider = styled.input `
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: var(--bg-tertiary);
  border-radius: 3px;
  outline: none;
  border: 1px solid var(--border-color);

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: var(--accent-color);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg-primary);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: var(--accent-color);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg-primary);
  }
`;
const ControlValue = styled.span `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 60px;
  text-align: right;
`;
const Select = styled.select `
  flex: 1;
  padding: 8px 12px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--border-color-hover);
  }

  &:focus {
    border-color: var(--accent-color);
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
const SummaryText = styled.p `
  font-size: 13px;
  color: var(--text-secondary);
  text-align: center;
  line-height: 1.6;
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  border: 1px solid var(--border-color);
`;
const ActionButtons = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const ActionButton = styled.button `
  flex: 1;
  padding: 12px 20px;
  background: ${props => props.$primary ? 'var(--accent-color)' : 'var(--bg-secondary)'};
  border: 2px solid ${props => props.$primary ? 'var(--accent-color)' : 'var(--border-color)'};
  border-radius: 4px;
  color: ${props => props.$primary ? 'var(--bg-primary)' : 'var(--text-primary)'};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.$primary ? 'var(--accent-color)' : 'var(--border-color-hover)'};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;
const PreviewPanel = styled.div `
  display: flex;
  flex-direction: column;
`;
const PreviewSubtitle = styled(Text) `
  margin-bottom: 16px;
`;
const PreviewContainer = styled.div `
  height: 600px;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
`;
const PreviewInfo = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;
const InfoItem = styled.div `
  padding: 12px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--border-color);
`;
const InfoLabel = styled.span `
  font-size: 12px;
  color: var(--text-tertiary);
`;
const InfoValue = styled.span `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;
const ToggleButton = styled.button `
  flex: 1;
  padding: 8px 12px;
  background: ${props => props.$active ? 'var(--accent-color)' : 'var(--bg-secondary)'};
  border: 2px solid ${props => props.$active ? 'var(--accent-color)' : 'var(--border-color)'};
  border-radius: 4px;
  color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-primary)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.$active ? 'var(--accent-color)' : 'var(--border-color-hover)'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;
export default DiamondCutDesigner;
